import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { type FormStepProps, OdysseyContext } from '@redventures/odyssey'
import { EnergyBillFileUpload } from '@strata-monorepo/shared-ui/components/EnergyBillFileUpload/EnergyBillFileUpload'
import { type BillEntryData } from '@strata-monorepo/shared-ui/components/BillEntry/BillEntry'
import { type CohesionGlobal } from '@strata-monorepo/shared-types/src/window'
import { useContainerState } from '../../context/ContainerQueryContext'
import { useStoreActions, useStoreState } from '../../store/useStore'
import { useProgressLoader } from '@strata-monorepo/shared-ui/hooks/useProgressLoader'
import useElementClicked from '@strata-monorepo/tracking/src/hooks/useElementClicked'
import useFieldInputted from '@strata-monorepo/tracking/src/hooks/useFieldInputted'
import {
  ActionOutcome,
  CustomDimensionsKeys,
  ElementType,
} from '@strata-monorepo/tracking/src/types'
import type { FormStepRef } from './formStepRef'
import { useIncense } from '../../hooks/useIncense'
import { EnergyBillHelpModal } from '../../components/EnergyBillHelpModal'
import { useMonarchRule } from '@redventures/cohesion-utils-react'
import { isBillFromPreviousMonth, createSyntheticBillEntry } from '../../utils/billDateUtils'

const ESTIMATED_MONTHLY_KWH_FIELD_KEY = 'estimatedMonthlyKwh'
const DEFAULT_SLIDER_KWH = 1000
const SLIDER_TRACKING_DEBOUNCE_MS = 400

interface UploadQuestionComponentProps {
  formValue: string
  tracking: {
    stepName: string
    stepNumber: number
    stepId: string
    fieldName: string
  }
}

const BillUploadQuestion = forwardRef<FormStepRef, FormStepProps>(({ formStep }, ref) => {
  const { componentProps } = formStep
  const { formValue, tracking } = componentProps as UploadQuestionComponentProps
  const odysseyContext = useContext(OdysseyContext)

  const rawSliderValue = odysseyContext?.getValue(ESTIMATED_MONTHLY_KWH_FIELD_KEY)
  const sliderValue = Number(rawSliderValue) || DEFAULT_SLIDER_KWH
  const { isMobile } = useContainerState()
  useMonarchRule<boolean>('fmpUploadBillLearnMoreLink')
  const [, setDisplayManualEntryNoLongerAddedMessage] = useState(false)
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false)
  const [sliderWasInteracted, setSliderWasInteracted] = useState(false)
  const {
    progress: loaderProgress,
    startLoader,
    complete: loaderComplete,
  } = useProgressLoader({
    maxProgress: 90,
    intervalMs: 200,
    incrementBy: 7,
  })
  const trackElementClicked = useElementClicked()
  const trackFieldInputted = useFieldInputted()
  const formId = useStoreState((state) => state.form.formId)
  const currentStepIndex = useStoreState((state) => state.form.currentStepIndex)
  const formType = useStoreState((state) => state.form.formType)

  const fetchConsumptionPrediction = useStoreActions(
    (actions) => actions.consumptionPrediction.fetchConsumptionPrediction,
  )

  const enrichCustomerResponse = useStoreState(
    (state) => state.enrichCustomer.enrichCustomerResponse,
  )
  const {
    manualEntryToggle,
    uploadedBillsModel,
    manualBillsModel,
    reconciledEntries,
    requiredFieldsManifest,
  } = useStoreState((state) => state.bill)

  const uploadedData = uploadedBillsModel.uploadedData
  const manualData = manualBillsModel.manualData

  const { uploadEnergyBill, setUploadedData, removeUploadedByIndex } = useStoreActions(
    (actions) => actions.bill.uploadedBillsModel,
  )

  const { setManualEntryToggle } = useStoreActions((actions) => actions.bill)

  const incense = useIncense()

  const sliderFieldInputtedDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (sliderFieldInputtedDebounceRef.current !== null) {
        clearTimeout(sliderFieldInputtedDebounceRef.current)
        sliderFieldInputtedDebounceRef.current = null
      }
    }
  }, [])

  const effectiveEntries = useMemo((): BillEntryData[] => {
    const hasValidUploadedBill = reconciledEntries.length > 0
    const anyBillFromPreviousMonth = reconciledEntries.some(isBillFromPreviousMonth)

    if (hasValidUploadedBill) {
      return reconciledEntries
    } else if (hasValidUploadedBill && !anyBillFromPreviousMonth) {
      return sliderWasInteracted ? [createSyntheticBillEntry(sliderValue)] : reconciledEntries
    }

    return sliderWasInteracted ? [createSyntheticBillEntry(sliderValue)] : reconciledEntries
  }, [reconciledEntries, sliderWasInteracted, sliderValue])

  // Expose callback function to parent via ref
  useImperativeHandle(ref, () => ({
    onBeforeNext: async () => {
      // Mark field as touched before proceeding
      odysseyContext?.setTouched(formValue)

      const cohesionData: CohesionGlobal = (window as Window)._Cohesion || {}

      try {
        if (enrichCustomerResponse) {
          await fetchConsumptionPrediction({
            cohesionData,
            enrichedData: enrichCustomerResponse,
            allEntries: effectiveEntries,
          })
        }
        return true // Allow navigation
      } catch (error) {
        incense(error)
          .captureDetails({
            name: 'Questionnaire.UploadQuestion.Error',
            message: 'Find My Plan questionnaire | Consumption API Fetch Error',
          })
          .sendError()

        // Redirect to error page when ticket is completed
        return false // Prevent navigation
      }
    },
  }))

  // This useEffect is to check whenever we disable manual entry, then uncheck the manual entry toggle
  useEffect(() => {
    if (manualData.length === 0) {
      setManualEntryToggle(false)
    }
  }, [manualData, setManualEntryToggle])

  useEffect(() => {
    if (manualData && manualData.length > 0 && !manualEntryToggle) {
      setDisplayManualEntryNoLongerAddedMessage(true)
    } else {
      setDisplayManualEntryNoLongerAddedMessage(false)
    }
  }, [manualEntryToggle, manualData, setDisplayManualEntryNoLongerAddedMessage])

  useEffect(() => {
    odysseyContext?.setValue(formValue, effectiveEntries)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveEntries, formValue])

  const handleFileUploadElementClickEvent = (): void => {
    trackElementClicked({
      clickType: ActionOutcome.Upload,
      elementType: ElementType.Button,
      location: tracking.stepId,
      itemTitle: 'Click to upload',
      itemName: 'upload_cta',
      customDimensions: [
        {
          key: CustomDimensionsKeys.FORM_ID,
          value: formId,
        },
        {
          key: CustomDimensionsKeys.STEP_ID,
          value: tracking.stepId,
        },
      ],
    })
  }

  const handleUploadEnergyBill = async (newFiles: FileList | File[]) => {
    startLoader()
    const cohesionData: CohesionGlobal = (window as Window)._Cohesion || { sessionId: '' }
    try {
      const uploadedBills = await uploadEnergyBill({ files: newFiles, cohesionData })
      loaderComplete(100)

      // Trigger tracking event for each successfully parsed bill (no errors)
      uploadedBills.forEach((billData) => {
        if (!billData.error) {
          handleSubmitFieldInputtedEvent(billData, 'auto')
        }
      })

      // Reset progress after a short delay to show completion
      setTimeout(() => {
        loaderComplete(0)
      }, 1000)
    } catch (error) {
      incense(error)
        .captureDetails({
          name: 'Questionnaire.UploadQuestion.Error',
          message: 'Find My Plan questionnaire | Upload Question Error',
        })
        .sendError()

      loaderComplete(0)
    }
  }

  const handleSubmitFieldInputtedEvent = (billData: BillEntryData, fieldSource: string): void => {
    const autofilled = fieldSource === 'auto'

    const fieldValue = {
      month: billData.month,
      day: billData.day,
      year: billData.year,
      address: billData.address,
      usage_kwh: billData.usage,
      billAmount: billData.billing,
    }

    trackFieldInputted({
      formContext: {
        formName: 'fmp',
        formType,
        formId,
      },
      stepContext: {
        stepId: tracking.stepId,
        stepName: tracking.stepName,
        stepNumber: currentStepIndex,
      },
      userInputField: {
        fieldName: tracking.fieldName,
        fieldType: 'text',
        fieldValue: JSON.stringify(fieldValue),
        fieldSource,
        autofilled,
      },
    })
  }

  const handleUploadEntryOnChange = (entries: BillEntryData[]) => {
    setUploadedData(entries)
  }

  const handleOnSliderChange = (value: number) => {
    odysseyContext?.setValue(ESTIMATED_MONTHLY_KWH_FIELD_KEY, value)

    if (sliderFieldInputtedDebounceRef.current !== null) {
      clearTimeout(sliderFieldInputtedDebounceRef.current)
    }

    sliderFieldInputtedDebounceRef.current = setTimeout(() => {
      sliderFieldInputtedDebounceRef.current = null

      trackFieldInputted({
        formContext: {
          formName: 'fmp',
          formType,
          formId,
        },
        stepContext: {
          stepId: tracking.stepId,
          stepName: tracking.stepName,
          stepNumber: currentStepIndex,
        },
        userInputField: {
          autofilled: false,
          fieldName: tracking.fieldName,
          fieldLabel: '',
          fieldType: 'text',
          fieldValue: JSON.stringify(value),
          fieldSource: 'manual',
        },
      })
    }, SLIDER_TRACKING_DEBOUNCE_MS)
  }

  return (
    <div className='strata:w-full'>
      <div className='strata:flex strata:flex-col strata:gap-y-6 strata:md:p-0 strata:lg:gap-y-4 strata:justify-center strata:items-center strata:md:gap-y-8'>
        {/* Learn more link */}
        <div className='strata:flex strata:flex-col strata:items-center strata:gap-y-2 strata:md:gap-y-4'>
          <h2 className='strata:text-title-base strata:@qm/qc:text-title-xl strata:font-bold strata:lg:whitespace-nowrap strata:lg:w-max strata:lg:max-w-[100vw] strata:lg:overflow-hidden strata:lg:text-ellipsis strata:lg:relative strata:lg:left-1/2 strata:lg:-translate-x-1/2'>
            What was your energy usage last month?
          </h2>
          <p className='strata:text-gray-600 strata:text-sm strata:md:text-base'>
            Learn where to find your usage.{' '}
            <button
              type='button'
              onClick={() => {
                setIsHelpModalOpen(true)
              }}
              className='strata:text-secondary-500 strata:underline hover:strata:text-secondary-600 strata:cursor-pointer strata:bg-transparent strata:border-none strata:p-0'
            >
              Learn more
            </button>
          </p>
        </div>

        <div className='strata:flex-1 strata:w-full'>
          <EnergyBillFileUpload
            onChange={handleUploadEnergyBill}
            onClick={handleFileUploadElementClickEvent}
            progress={loaderProgress}
            sliderValue={sliderValue}
            onSliderChange={handleOnSliderChange}
            onSliderInteracted={() => setSliderWasInteracted(true)}
            uploadedBills={uploadedData}
            requiredFields={requiredFieldsManifest}
            onUpdateUploadedBills={handleUploadEntryOnChange}
            onDeleteUploadedBill={removeUploadedByIndex}
            onSubmitUploadedEdit={(billData, fieldSource) =>
              handleSubmitFieldInputtedEvent(billData, fieldSource)
            }
            isMobile={isMobile}
            entryFormDisabledFields={['planName', 'day']}
          />
        </div>
      </div>
      <EnergyBillHelpModal
        isMobile={isMobile}
        open={isHelpModalOpen}
        onOpenChange={setIsHelpModalOpen}
      />
    </div>
  )
})

BillUploadQuestion.displayName = 'BillUploadQuestion'
export default BillUploadQuestion
